import React, { useMemo, useState, type JSX } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { RecognitionAuditDetailPanel } from "./recognition-audit-detail-panel";
import { RecognitionModelComparisonPanel } from "./recognition-model-comparison-panel";
import { RecognitionRawArtifactViewer, type RecognitionRawArtifact } from "./recognition-raw-artifact-viewer";
import {
  toRecognitionModelComparisonViewModel,
} from "./recognition-model-comparison-view-model";
import {
  useRecognitionAuditDetailScreenModel,
} from "./use-recognition-audit-screen-model";

export type RecognitionAuditDetailScreenProps = {
  dependencies: AdminDashboardDependencies;
};

export function buildRecognitionAuditListPath(searchParams: URLSearchParams): string {
  const query = searchParams.toString();
  return query.length > 0 ? `/admin/recognition-audits?${query}` : "/admin/recognition-audits";
}

export function RecognitionAuditNotFoundState(props: { listRoute: string }): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const copy = {
    body: pickAdminDashboardLocaleText(locale, {
      en: "The recognition run may have been deleted or is outside the current admin read scope.",
      ko: "인식 실행이 삭제되었거나 현재 관리자 조회 범위 밖에 있습니다.",
    }),
    title: pickAdminDashboardLocaleText(locale, {
      en: "The requested recognition run could not be found.",
      ko: "요청한 인식 실행을 찾을 수 없습니다.",
    }),
    back: pickAdminDashboardLocaleText(locale, { en: "Back to list", ko: "목록으로" }),
  };

  return <AdminEmptyState title={copy.title} body={copy.body} action={<Link to={props.listRoute}>{copy.back}</Link>} />;
}

function RecognitionAuditDetailScreenContent(props: {
  dependencies: AdminDashboardDependencies;
  listRoute: string;
  locale: "ko" | "en";
  recognitionRunId: string;
}): JSX.Element {
  const [activeRawArtifacts, setActiveRawArtifacts] = useState<readonly RecognitionRawArtifact[] | undefined>();
  const state = useRecognitionAuditDetailScreenModel(
    useMemo(() => ({ ...props.dependencies, locale: props.locale }), [props.dependencies, props.locale]),
    props.recognitionRunId,
  );
  const copy = {
    back: pickAdminDashboardLocaleText(props.locale, { en: "Back to list", ko: "목록으로" }),
    loading: pickAdminDashboardLocaleText(props.locale, { en: "Loading recognition audit detail...", ko: "인식 감사 상세를 불러오는 중입니다..." }),
    failed: pickAdminDashboardLocaleText(props.locale, { en: "Recognition audit detail failed", ko: "인식 감사 상세 조회 실패" }),
    singlePathUsage: pickAdminDashboardLocaleText(props.locale, { en: "Single-path usage", ko: "기본 단일 실행 모델" }),
  };

  if (state.loadState.status === "idle" || state.loadState.status === "loading") {
    return <AdminEmptyState title={copy.loading} body={copy.loading} />;
  }
  if (state.loadState.status === "permission_denied") {
    return <AdminErrorState title={state.loadState.error.message} tone="permission_denied" action={<Link to={props.listRoute}>{copy.back}</Link>} />;
  }
  if (state.error?.code === "ADMIN_RECOGNITION_AUDIT_NOT_FOUND") {
    return <RecognitionAuditNotFoundState listRoute={props.listRoute} />;
  }
  if (state.loadState.status === "error" || !state.detail || !state.detailData) {
    return <AdminErrorState title={copy.failed} body={state.error?.message} action={<Link to={props.listRoute}>{copy.back}</Link>} />;
  }

  const comparison = toRecognitionModelComparisonViewModel(state.detailData, {
    locale: props.locale,
    permissions: props.dependencies.adminPermissions,
  });
  const technicalCopy = pickAdminDashboardLocaleText(props.locale, { en: "Full audit information", ko: "전체 감사 정보" });

  return (
    <section className="recognition-audit-detail" style={{ display: "grid", gap: "16px", width: "100%", maxWidth: "1180px" }}>
      <style>{`
        .recognition-audit-detail__summary { display: grid; gap: 14px; padding: 18px; border: 1px solid rgba(24,33,43,.1); border-radius: 18px; background: rgba(255,255,255,.86); box-shadow: 0 12px 30px rgba(15,23,42,.06); }
        .recognition-audit-detail__metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
        .recognition-audit-detail__metric { padding: 12px; border-radius: 12px; background: #f8fafc; min-width: 0; }
        .recognition-audit-detail__metric-label { display: block; color: #64748b; font-size: .78rem; font-weight: 700; }
        .recognition-audit-detail__metric-value { display: block; margin-top: 4px; color: #18212b; font-size: 1rem; font-weight: 800; overflow-wrap: anywhere; }
        .recognition-audit-detail__metric-context { display: block; margin-top: 4px; color: #64748b; font-size: .74rem; overflow-wrap: anywhere; }
        .recognition-audit-detail__technical { border: 1px solid rgba(24,33,43,.1); border-radius: 18px; background: rgba(255,255,255,.62); overflow: hidden; }
        .recognition-audit-detail__technical > summary { padding: 16px 18px; cursor: pointer; font-weight: 800; }
        .recognition-audit-detail__technical > div { padding: 0 12px 12px; }
        @media (max-width: 600px) { .recognition-audit-detail__metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); } .recognition-audit-detail__summary { padding: 14px; } }
      `}</style>
      <Link style={{ fontWeight: 700 }} to={props.listRoute}>{copy.back}</Link>
      <article className="recognition-audit-detail__summary" aria-label={pickAdminDashboardLocaleText(props.locale, { en: "Recognition run summary", ko: "인식 실행 요약" })}>
        <div>
          <strong style={{ fontSize: "1.08rem" }}>{state.detailData.lifecycle.status}</strong>
          <p style={{ margin: "6px 0 0", color: "#334155", fontWeight: 700 }}>{state.detailData.lifecycle.outcome}</p>
          <p style={{ margin: "6px 0 0", color: "#64748b" }}>{formatAdminDate(state.detailData.lifecycle.startedAt)}</p>
          <p style={{ margin: "6px 0 0", color: "#475569", overflowWrap: "anywhere" }}>{state.detailData.sources.map((source) => source.fileName).join(", ")}</p>
        </div>
        <div className="recognition-audit-detail__metrics">
          {state.detail.usageSummary.map((metric) => (
            <div className="recognition-audit-detail__metric" key={metric.id}>
              <span className="recognition-audit-detail__metric-label">{metric.label}</span>
              <span className="recognition-audit-detail__metric-value">{metric.valueLabel}</span>
              {metric.contextLabel ? <span className="recognition-audit-detail__metric-context">{metric.contextLabel}</span> : null}
            </div>
          ))}
        </div>
      </article>
      {comparison.legacyMessage ? <AdminEmptyState title={comparison.legacyMessage} body={comparison.legacyMessage} /> : (
        <RecognitionModelComparisonPanel
          cards={comparison.cards}
          groupStatusLabel={comparison.groupStatusLabel}
          locale={props.locale}
          onRequestRawAccess={(artifact) => setActiveRawArtifacts([artifact])}
          rawJsonComparison={comparison.rawJsonComparison}
          rawVisibility={comparison.rawVisibility}
          resultMatrix={comparison.resultMatrix}
        />
      )}
      {comparison.singlePathUsage ? <p style={{ margin: 0 }}>{copy.singlePathUsage}: {comparison.singlePathUsage.provider} / {comparison.singlePathUsage.model} / {comparison.singlePathUsage.requestStatusLabel}</p> : null}
      <details className="recognition-audit-detail__technical">
        <summary>{technicalCopy}</summary>
        <div>
          <RecognitionAuditDetailPanel
            detail={state.detailData}
            locale={props.locale}
            onRequestRawAccess={(artifact) => setActiveRawArtifacts([artifact])}
            viewModel={state.detail}
          />
        </div>
      </details>
      {activeRawArtifacts ? <RecognitionRawArtifactViewer artifacts={activeRawArtifacts} key={activeRawArtifacts.map((artifact) => `${artifact.id}:${artifact.action.targetId ?? ""}`).join("|")} locale={props.locale} onClose={() => setActiveRawArtifacts(undefined)} onExpired={() => setActiveRawArtifacts(undefined)} recognitionRunId={props.recognitionRunId} requestRawAccess={state.submitRawAccess} /> : null}
    </section>
  );
}

export function RecognitionAuditDetailScreen(props: RecognitionAuditDetailScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const params = useParams<{ recognitionRunId: string }>();
  const [searchParams] = useSearchParams();
  const listRoute = buildRecognitionAuditListPath(searchParams);
  const recognitionRunId = params.recognitionRunId;
  const missingCopy = pickAdminDashboardLocaleText(locale, {
    en: "Recognition run ID is missing.",
    ko: "인식 실행 ID가 없습니다.",
  });
  const backCopy = pickAdminDashboardLocaleText(locale, { en: "Back to list", ko: "목록으로" });

  if (!recognitionRunId) {
    return <AdminErrorState title={missingCopy} action={<Link to={listRoute}>{backCopy}</Link>} />;
  }

  return (
    <RecognitionAuditDetailScreenContent
      dependencies={props.dependencies}
      listRoute={listRoute}
      locale={locale}
      recognitionRunId={recognitionRunId}
    />
  );
}
